import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { fetchSiteEvents, Event, fetchSiteDetail, Site, fetchSiteCategories, Category, fetchSiteTags, Tag } from "@/services/customerService";
import KujiCard from "@/components/KujiCard";
import PhoneVerificationDialog from "@/components/PhoneVerificationDialog";
import { useAuth } from "@/components/AuthContext";
import { Package } from "lucide-react";

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

interface KujiTopPageProps {
  siteId: number;
  requirePhoneVerification?: boolean;
}

const ALL_CATEGORY: Category = { id: 0, name: "ALL", slug: "all" };

const KujiTopPage: React.FC<KujiTopPageProps> = ({ siteId, requirePhoneVerification }) => {
  const { currentUser, refreshUser } = useAuth();
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(ALL_CATEGORY);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [siteDetails, setSiteDetails] = useState<Site | null>(null);

  const [events, setEvents] = useState<Event[]>([]);
  const [filteredByStatus, setFilteredByStatus] = useState<Event[]>([]);
  // URL の ?status= から初期値を復元（デフォルト: on_sale）
  const initialStatus = searchParams.get('status') || 'on_sale';
  const [selectedStatus, setSelectedStatus] = useState<string>(initialStatus);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPhoneVerificationDialog, setShowPhoneVerificationDialog] = useState(false);

  useEffect(() => {
    const loadSiteDetails = async () => {
      try {
        const siteData = await fetchSiteDetail(siteId);
        setSiteDetails(siteData);
      } catch (err) {
        console.error(err);
        setError("店舗情報の取得に失敗しました");
      }
    };

    const loadData = async () => {
      try {
        const [categoriesData, tagsData] = await Promise.all([
          fetchSiteCategories(siteId),
          fetchSiteTags(siteId),
        ]);
        const allCategories = [ALL_CATEGORY, ...categoriesData];
        setCategories(allCategories);
        setTags(tagsData);

        // URL のカテゴリー slug から選択状態を復元
        if (categorySlug) {
          const found = allCategories.find((c) => c.slug === categorySlug);
          if (found) {
            setSelectedCategory(found);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadSiteDetails();
    loadData();
  }, [siteId, categorySlug]);

  const loadEvents = async (categorySlug?: string, tagSlugs?: string[]) => {
    setError(null);
    setEventsLoading(true);
    try {
      const data = await fetchSiteEvents(
        siteId,
        categorySlug === "all" ? undefined : categorySlug,
        tagSlugs
      );
      // くじのみをフィルタリング
      const kujiEvents = data.filter((event: Event) => event.event_type === 'kuji');
      setEvents(kujiEvents);
    } catch (err: any) {
      setError(err.message || "イベントの取得に失敗しました");
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(selectedCategory?.slug, selectedTags.map((tag) => tag.slug));
  }, [selectedCategory, selectedTags, siteId]);

  // ステータスフィルター
  useEffect(() => {
    const now = new Date();

    // 期間判定
    const getPeriodStatus = (event: Event): 'before' | 'during' | 'after' | 'no_period' => {
      const startsAt = event.starts_at ? new Date(event.starts_at) : null;
      const endsAt = event.ends_at ? new Date(event.ends_at) : null;
      if (!startsAt && !endsAt) return 'no_period';
      if (startsAt && now < startsAt) return 'before';
      if (endsAt && now > endsAt) return 'after';
      return 'during';
    };

    // 完売判定（新旧両方式対応）
    const isSoldOut = (event: Event) => {
      const totalStock = event.total_stock_count ?? event.lottery_pools_count ?? 0;
      const availableStock = event.available_stock_count ?? event.lottery_pools_available_count ?? 0;
      return totalStock > 0 && availableStock === 0;
    };

    if (selectedStatus === "all") {
      setFilteredByStatus(events);
    } else {
      const filtered = events.filter((event) => {
        const periodStatus = getPeriodStatus(event);

        if (selectedStatus === "on_sale") {
          // 販売中: active/on_sale かつ 完売していない かつ 期間内
          const isActive = event.status === "active" || event.status === "on_sale";
          const withinPeriod = periodStatus === 'during' || periodStatus === 'no_period';
          return isActive && !isSoldOut(event) && withinPeriod;
        } else if (selectedStatus === "scheduled") {
          // 販売予定: scheduled または 開催前
          return event.status === "scheduled" || periodStatus === 'before';
        } else if (selectedStatus === "ended") {
          // 販売終了: ended/finished または 完売 または 期間終了
          return event.status === "ended" || event.status === "finished" || isSoldOut(event) || periodStatus === 'after';
        }
        return true;
      });
      setFilteredByStatus(filtered);
    }
  }, [events, selectedStatus]);

  // 電話番号未認証チェック（ページ表示時に1回のみ、店舗設定で必須の場合のみ）
  useEffect(() => {
    if (!currentUser) return;
    if (!requirePhoneVerification) return;

    // 既に認証済みの場合はスキップ
    if (currentUser.phone_verified) return;

    // 今回のセッションで既にスキップ済みかチェック
    const dismissedKey = `phone_verification_dismissed_${currentUser.id}`;
    const dismissed = sessionStorage.getItem(dismissedKey);

    if (!dismissed) {
      // 少し遅延させて表示（UX向上のため）
      const timer = setTimeout(() => {
        setShowPhoneVerificationDialog(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [currentUser, requirePhoneVerification]);

  const handlePhoneVerificationSuccess = async () => {
    setShowPhoneVerificationDialog(false);
    await refreshUser();
  };

  const handlePhoneVerificationClose = () => {
    setShowPhoneVerificationDialog(false);
    // セッション中はスキップ
    if (currentUser) {
      const dismissedKey = `phone_verification_dismissed_${currentUser.id}`;
      sessionStorage.setItem(dismissedKey, 'true');
    }
  };

  return (
    <>
      <Helmet>
        <title>{siteDetails?.name ? `${siteDetails.name} | くじ一覧` : 'くじ一覧'}</title>
        <meta name="description" content={`${siteDetails?.name || ''}で開催中のくじ一覧`} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="container mx-auto max-w-screen-lg px-4 pb-4 pt-20">
          {/* バナースライダー */}
          {(() => {
            const topBanners = siteDetails?.banners?.filter((b: { placement?: string }) => !b.placement || b.placement === 'top') || [];
            return topBanners.length > 0 && (
            <div className="mb-6">
              <div className="rounded-theme-lg border-theme shadow-theme overflow-hidden">
              <Swiper
                modules={[Autoplay, Pagination]}
                spaceBetween={0}
                slidesPerView={1}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                  el: '.banner-pagination',
                }}
                loop={topBanners.length > 1}
              >
                {topBanners.map((banner: { id: number; image_url: string | null; image_variant_urls?: Record<string, string> | null; link_url: string | null }) => (
                  <SwiperSlide key={banner.id}>
                    {banner.link_url ? (
                      <a
                        href={banner.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={banner.image_url || ''}
                          alt="バナー"
                          className="w-full h-auto object-cover"
                        />
                      </a>
                    ) : (
                      <img
                        src={banner.image_url || ''}
                        alt="バナー"
                        className="w-full h-auto object-cover"
                      />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
              </div>
              <div className="banner-pagination flex justify-center gap-1.5 mt-2" />
            </div>
            );
          })()}

          {/* カテゴリー */}
          <div className="mt-4 overflow-x-auto whitespace-nowrap scrollbar-hide">
            <nav className="flex gap-2" aria-label="Tabs">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={category.slug === "all" ? "/" : `/c/${category.slug}`}
                  className={`px-4 py-2 rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm border-theme transition-all ${
                    selectedCategory?.slug === category.slug
                      ? "bg-theme-primary text-theme-button shadow-theme-sm"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCategory(category);
                    const basePath = category.slug === "all" ? "/" : `/c/${category.slug}`;
                    const query = selectedStatus !== 'on_sale' ? `?status=${selectedStatus}` : '';
                    navigate(`${basePath}${query}`);
                  }}
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* タグ */}
          {tags.length > 0 && (
            <div className="mt-4">
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const isSelected = selectedTags.find((selectedTag) => selectedTag.slug === tag.slug);
                  return (
                    <button
                      key={tag.slug}
                      className={`px-3 py-1.5 rounded-full [font-weight:var(--theme-font-weight-button)] text-xs border-theme transition-all ${
                        isSelected
                          ? "bg-pink-400 text-black shadow-theme-sm"
                          : "bg-white text-gray-600 hover:bg-pink-100"
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedTags(
                            selectedTags.filter((selectedTag) => selectedTag.slug !== tag.slug)
                          );
                        } else {
                          setSelectedTags([...selectedTags, tag]);
                        }
                      }}
                    >
                      #{tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ステータスフィルター */}
          <div className="mt-4 flex justify-end">
            <div className="flex items-center gap-3 text-sm">
              {[
                { value: "on_sale", label: "販売中" },
                { value: "scheduled", label: "販売予定" },
                { value: "ended", label: "販売終了" },
              ].map((status) => (
                <label
                  key={status.value}
                  className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-theme transition-all ${
                    selectedStatus === status.value
                      ? "bg-theme-primary/10"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="status-filter"
                    value={status.value}
                    checked={selectedStatus === status.value}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      setSelectedStatus(newStatus);
                      // URL クエリにステータスを反映（デフォルト on_sale の場合はクエリなし）
                      const basePath = categorySlug ? `/c/${categorySlug}` : '/';
                      const query = newStatus === 'on_sale' ? '' : `?status=${newStatus}`;
                      navigate(`${basePath}${query}`, { replace: true });
                    }}
                    className="w-3.5 h-3.5 accent-[var(--theme-primary-color)] cursor-pointer"
                  />
                  <span className={`text-xs [font-weight:var(--theme-font-weight-button)] ${
                    selectedStatus === status.value
                      ? "text-theme-primary"
                      : "text-gray-600"
                  }`}>
                    {status.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* イベント一覧 - くじのみ表示 */}
          <div className="mt-6">
            {eventsLoading ? (
              <div className="flex justify-center items-center w-full py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
              </div>
            ) : filteredByStatus.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                {filteredByStatus.map((event) => (
                  <KujiCard
                    key={event.uid}
                    id={event.id}
                    siteId={siteId.toString()}
                    siteSlug={siteDetails?.slug}
                    uid={event.uid}
                    name={event.name}
                    description={event.description}
                    imageUrl={event.thumbnail_url || "/placeholder.svg"}
                    imageVariants={event.thumbnail_variant_urls}
                    status={event.status}
                    status_label={event.status_label}
                    starts_at={event.starts_at}
                    ends_at={event.ends_at}
                    soldOut={(event.total_stock_count ?? event.lottery_pools_count ?? 0) > 0 && (event.available_stock_count ?? event.lottery_pools_available_count ?? 0) === 0}
                  />
                ))}
              </div>
            ) : (
              <div className="flex justify-center items-center w-full py-16">
                <div className="bg-white border-4 border-dashed border-gray-300 rounded-theme-lg p-8 text-center w-full max-w-md">
                  <div className="w-16 h-16 bg-gray-100 border-2 border-gray-300 rounded-theme-lg flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 [font-weight:var(--theme-font-weight-button)] text-lg">開催中のスクラッチはまだありません</p>
                  <p className="text-gray-400 text-sm mt-2">新しいスクラッチが追加されるまでお待ちください</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 電話番号認証モーダル */}
      <PhoneVerificationDialog
        isOpen={showPhoneVerificationDialog}
        onClose={handlePhoneVerificationClose}
        onSuccess={handlePhoneVerificationSuccess}
      />
    </>
  );
};

export default KujiTopPage;
